import React from 'react';
import { get } from 'lodash-es';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { CmsModel, CmsPaths } from 'client/data/models/cms';
import { parseContent } from 'client/data/cms/content';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { setCriticalCss } from 'client/actions/critical-css';
import { getSeoObject } from 'site-modules/editorial/pages/research-article/seo';
import { getBestSeoObject } from 'site-modules/editorial/pages/best/seo';
import { handle404Error } from 'site-modules/shared/utils/errors';
import { isBestContent } from 'site-modules/editorial/utils/best';
import { getBestKeys } from 'site-modules/editorial/utils/article-critical-css-keys';
import { getBestOfArticlesIndexPath, getBestOfArticlesConfigPath } from 'site-modules/editorial/utils/content-path';
import { removeSpecialCharacters } from 'site-modules/shared/utils/string';
import { RESEARCH_LINK_WIDGET_EDITORIAL_PATH } from 'site-modules/shared/constants/editorial/research-article';
import { ENTRY_RENDER_TYPE } from 'site-modules/editorial/constants/entry-render';

export function getPageName(slug, prefix) {
  return `${prefix ? `${prefix}_` : ''}advice_${slug}_article`;
}

export const getPageOptions = ({ bestOfCategory, articleId, chunkName, pageTemplate: PageTemplate }) => ({
  name: getPageName(bestOfCategory),
  category: 'advice',
  chunkName,
  adsCustomTargeting: {
    sect1: removeSpecialCharacters(articleId || bestOfCategory),
  },
  seo: (state, props) => {
    const contentPathInState = getBestOfArticlesIndexPath(bestOfCategory, articleId);
    const content = parseContent(state.cms.content[contentPathInState]);

    return isBestContent(content.id)
      ? getBestSeoObject(content, props.location.pathname)
      : getSeoObject(state, contentPathInState, bestOfCategory, articleId);
  },
  preload: store => {
    const preloader = new ModelPreloader(store);
    const articlePath = getBestOfArticlesIndexPath(bestOfCategory, articleId);
    preloader.resolve(`content["${articlePath}"]`, CmsModel);
    return preloader.load().then(modelState => {
      const article = modelState.get(`content["${articlePath}"]`, CmsModel);

      if (!(article && article.id)) {
        throw handle404Error({ errorMessage: `No content was found for ${articlePath}` });
      }

      const isBestContentId = isBestContent(article.id);
      if (!isBestContentId) {
        preloader.resolve(`content["${getBestOfArticlesConfigPath(bestOfCategory, articleId)}"]`, CmsModel);
        preloader.resolve(CmsPaths.buildAllAuthorsPath({ contentPath: articlePath }), CmsModel);
      } else {
        preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
      }

      const articleContent = parseContent(article);
      const isOneColumnTemplate = articleContent.metadata('oneColumnTemplate').boolean();
      const hasHVSModule = articleContent
        .child('main')
        .children()
        .some(child => child.id === ENTRY_RENDER_TYPE.HVS_MODULE);
      if (hasHVSModule) {
        preloader.resolve(`content["${RESEARCH_LINK_WIDGET_EDITORIAL_PATH}"]`, CmsModel);
      }

      const edwPrefix = get(article, 'contentMetadata.edwPrefix');
      store.dispatch(
        setCriticalCss({
          ...getBestKeys(parseContent(articleContent)),
          isOneColumnTemplate,
        })
      );
      if (edwPrefix) {
        store.dispatch(
          legacyPageUpdate({
            pageName: getPageName(bestOfCategory, article.contentMetadata.edwPrefix),
          })
        );
      }

      return preloader.load();
    });
  },
  component: () => <PageTemplate slug={bestOfCategory} articleId={articleId} />,
});
